import { useCallback, useMemo } from 'react';
import { PERMISSIONS } from 'shared/constants';
import { AppPermission } from 'shared/types';
import { getAppPermissions } from 'shared/utility';

export function usePermissions(claims = '') {
    const permissions: AppPermission[] = useMemo(() => getAppPermissions(claims), [claims]);

    const hasPermission = useCallback(
        (appPermission: AppPermission) => {
            return permissions.includes(appPermission);
        },
        [permissions]
    );

    const isAddProductAllowed = useMemo(() => hasPermission(PERMISSIONS['add-product']), [hasPermission]);
    const isApproveCustomerTierOverrideAllowed = useMemo(
        () => hasPermission(PERMISSIONS['approve-customer-tier-override']),
        [hasPermission]
    );
    const isApproveDeviationAllowed = useMemo(() => hasPermission(PERMISSIONS['approve-deviation']), [hasPermission]);
    const isApproveMarketPriceAllowed = useMemo(
        () => hasPermission(PERMISSIONS['approve-market-price']),
        [hasPermission]
    );
    const isMassEditMarketPricesAllowed = useMemo(
        () => hasPermission(PERMISSIONS['mass-edit-market-prices']),
        [hasPermission]
    );
    const isMassEditDeviationsAllowed = useMemo(
        () => hasPermission(PERMISSIONS['mass-edit-deviations']),
        [hasPermission]
    );
    const isPageActivePriceManagementAllowed = useMemo(
        () => hasPermission(PERMISSIONS['page-active-price-management.read']),
        [hasPermission]
    );
    const isPageActivePriceManagementEditAllowed = useMemo(
        () => hasPermission(PERMISSIONS['page-active-price-management']),
        [hasPermission]
    );
    const isPageFuturePriceManagementAllowed = useMemo(
        () => hasPermission(PERMISSIONS['page-future-price-management.read']),
        [hasPermission]
    );
    const isPageProductManagementAllowed = useMemo(
        () => hasPermission(PERMISSIONS['page-product-management']),
        [hasPermission]
    );
    const isPageSettingsAllowed = useMemo(() => hasPermission(PERMISSIONS['page-settings']), [hasPermission]);
    const isViewAllDistributorsAllowed = useMemo(
        () => hasPermission(PERMISSIONS['view-all-distributors']),
        [hasPermission]
    );
    const isExpireMarketPriceAllowed = useMemo(
        () => hasPermission(PERMISSIONS['expire-market-price']),
        [hasPermission]
    );

    return {
        permissions,
        hasPermission,
        isAddProductAllowed,
        isApproveCustomerTierOverrideAllowed,
        isApproveDeviationAllowed,
        isApproveMarketPriceAllowed,
        isMassEditMarketPricesAllowed,
        isMassEditDeviationsAllowed,
        isPageActivePriceManagementAllowed,
        isPageActivePriceManagementEditAllowed,
        isPageFuturePriceManagementAllowed,
        isPageProductManagementAllowed,
        isPageSettingsAllowed,
        isViewAllDistributorsAllowed,
        isExpireMarketPriceAllowed,
    };
}
